/**
 * etagCache.js — client-side ETag cache for axios.
 *
 * HIPAA-safe poll optimization. The browser's own cache may be disabled by
 * upstream proxies (e.g., Cloudflare rewriting Cache-Control to `no-store`),
 * so this module gives us a deterministic, app-controlled layer that stores
 * response bodies keyed by (URL + Authorization) and automatically:
 *
 *   1. Attaches ``If-None-Match`` to outbound GETs when we have a cached copy.
 *   2. On a 304 response, replaces the body with the cached copy, so callers
 *      see a normal 200-shaped response and no React code has to change.
 *   3. On a 200 response carrying an ``ETag`` header, stores body + ETag.
 *
 * HIPAA / PHI safety
 * ------------------
 * - Cache is in-memory only (never written to localStorage or IndexedDB).
 * - Cleared on logout via ``clearEtagCache()``.
 * - Keyed by the Authorization header hash so two users sharing a browser
 *   tab cannot accidentally see each other's cached responses.
 * - Auth still runs on every request — the server returns 304 only after
 *   passing its own auth check.
 * - Only whitelisted URLs participate (config-only + per-user polls).
 */

// Whitelist of URL path prefixes/exact matches that may participate in
// ETag caching. Anything outside this list is bypassed.
//
// Rules for inclusion:
//   - GET-only
//   - Response must be deterministic for a given user+query
//   - Must match the backend ETag middleware whitelist (server decides
//     whether to emit an ETag header anyway; this is just a client-side gate
//     to avoid wasted If-None-Match headers).
const ETAG_WHITELIST = [
  '/api/notifications',
  '/api/staff/announcements',
  '/api/staff/messages',
  '/api/alerts/unread-counts',
  '/api/admin/branding-settings',
  '/api/billing/settings',
  '/api/form-templates',
  '/api/cpt-conflict-rules',
  '/api/insurance-cpt-restrictions',
  '/api/curricula',
  '/api/roles',
  '/api/payment-reminder-settings',
  '/api/auto-eligibility-settings',
  '/api/admin/client-portal-settings',
  '/api/insurances',
  '/api/treatment-planning/service-types',
  '/api/treatment-plans/my-reviews-due',
  '/api/documentation-deadlines/my-incomplete',
  '/api/documentation-deadlines/scheduled-reviews',
  '/api/releases/expiring-soon',
];

// In-memory Map<cacheKey, { etag, data, headers, storedAt }>
const _cache = new Map();

// Soft upper bound; LRU-ish eviction if we exceed it.
const MAX_ENTRIES = 200;

// Quick hash of Authorization header (token) so the cache key stays
// user-scoped without storing the raw token.
const _hashAuth = (auth) => {
  if (!auth) return 'anon';
  let h = 0;
  for (let i = 0; i < auth.length; i++) {
    h = (h * 31 + auth.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
};

const _isWhitelisted = (url) => {
  if (!url) return false;
  // Strip query string for the whitelist check, but keep full URL for cache key.
  const path = url.split('?')[0].replace(/^https?:\/\/[^/]+/, '');
  return ETAG_WHITELIST.some((p) => path === p || path.startsWith(`${p}/`));
};

const _cacheKey = (config) => {
  const method = (config.method || 'get').toLowerCase();
  const url = config.url || '';
  const full = config.baseURL ? `${config.baseURL}${url.startsWith('/') ? '' : '/'}${url}` : url;
  const auth = (config.headers?.Authorization || config.headers?.authorization || '').toString();
  return `${method}|${full}|${_hashAuth(auth)}`;
};

const _evictIfFull = () => {
  if (_cache.size <= MAX_ENTRIES) return;
  // Evict the oldest ~10% by insertion order.
  const toDrop = Math.ceil(_cache.size * 0.1);
  let dropped = 0;
  for (const key of _cache.keys()) {
    _cache.delete(key);
    dropped += 1;
    if (dropped >= toDrop) break;
  }
};

/**
 * axios request interceptor. Attaches ``If-None-Match`` when we have a
 * cached entry for this GET. Stores the key on the config so the response
 * interceptor can find the cached body on 304.
 */
export const etagRequestInterceptor = (config) => {
  try {
    if ((config.method || 'get').toLowerCase() !== 'get') return config;
    if (!_isWhitelisted(config.url)) return config;
    const key = _cacheKey(config);
    const entry = _cache.get(key);
    if (entry && entry.etag) {
      config.headers = config.headers || {};
      // Don't clobber an explicitly-set If-None-Match on the caller.
      if (!config.headers['If-None-Match'] && !config.headers['if-none-match']) {
        config.headers['If-None-Match'] = entry.etag;
      }
    }
    // Stash for the response interceptor.
    config.__etagCacheKey = key;
  } catch {
    // Never break a request because of caching.
  }
  return config;
};

/**
 * axios response interceptor (success path). Captures ETag from 200
 * responses and stores body for reuse on future 304s.
 */
export const etagResponseInterceptor = (response) => {
  try {
    const cfg = response.config || {};
    const key = cfg.__etagCacheKey;
    if (!key) return response;
    const etag = response.headers?.etag || response.headers?.ETag;
    if (etag && response.status === 200) {
      _cache.set(key, {
        etag,
        data: response.data,
        headers: response.headers,
        storedAt: Date.now(),
      });
      _evictIfFull();
    }
  } catch {
    // fail-open
  }
  return response;
};

/**
 * axios error interceptor. Handles the 304 case: axios raises an error for
 * 304 (some versions treat it as non-2xx). We catch, substitute the cached
 * body, and return a fabricated success response.
 *
 * Some axios builds return 304 as success (resolved promise). Handle both.
 */
export const etagErrorInterceptor = async (error) => {
  const response = error?.response;
  if (!response || response.status !== 304) {
    return Promise.reject(error);
  }
  const cfg = response.config || error?.config || {};
  const key = cfg.__etagCacheKey;
  if (!key) return Promise.reject(error);
  const entry = _cache.get(key);
  if (!entry) {
    // We got a 304 but have no cached body — nothing we can do; bubble up.
    return Promise.reject(error);
  }
  // Return a synthetic 200 response with the cached payload.
  return {
    data: entry.data,
    status: 200,
    statusText: 'OK (from etag cache)',
    headers: { ...entry.headers, 'x-cache': 'HIT' },
    config: cfg,
    request: response.request,
    __fromEtagCache: true,
  };
};

/**
 * Also support the "304 as success" branch some axios versions use.
 */
export const etagResponseInterceptor304 = (response) => {
  if (response.status !== 304) return response;
  const cfg = response.config || {};
  const key = cfg.__etagCacheKey;
  const entry = key ? _cache.get(key) : null;
  if (!entry) return response;
  return {
    ...response,
    status: 200,
    statusText: 'OK (from etag cache)',
    data: entry.data,
    headers: { ...response.headers, 'x-cache': 'HIT' },
    __fromEtagCache: true,
  };
};

/** Flush the entire cache (on logout, user switch, etc.). */
export const clearEtagCache = () => {
  _cache.clear();
};

/** Testing + debugging helpers (unused in prod). */
export const _etagCacheSize = () => _cache.size;
export const _etagCacheDump = () => Array.from(_cache.entries());
